import React, { useEffect } from "react";

import { cn } from "../lib/utils";

/**
 * Bottom-sheet drawer for mobile contextual content (Push K Slice 2 —
 * MapPage's "Active Issues" sidebar). Slides up from the bottom, takes
 * a configurable fraction of the viewport, has a drag-handle affordance.
 *
 * Hand-rolled (no headless-ui dep) — same shape as `Drawer.tsx` but
 * vertical. The sheet content area is internally scrollable so a long
 * list (many open outages) doesn't push the rest of the page off-screen.
 *
 * Closes on:
 *   - backdrop tap
 *   - Escape key
 *   - explicit `onClose` from caller
 *
 * Tap-the-handle to close is also wired up — the standard mobile
 * affordance for "drag down to dismiss" without writing actual drag
 * gesture code (kept simple).
 */
interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  /** Optional title rendered next to the drag handle. */
  title?: React.ReactNode;
  children: React.ReactNode;
  /** Sheet height as a viewport-height fraction. Default `60` (60vh). */
  heightVh?: number;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onClose,
  title,
  children,
  heightVh = 60,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while the sheet is open so taps that hit the
    // backdrop don't accidentally drag the page underneath. Matches
    // Modal.tsx behavior.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop. z-[1090] sits above Leaflet's panes + controls
          (max z-index ~1000) so the map page doesn't punch through. */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className={cn(
          "fixed inset-0 bg-black/40 z-[1090] transition-opacity lg:hidden",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
      />
      {/* Sheet — z-[1100] above the backdrop. */}
      <div
        role="dialog"
        aria-modal="true"
        style={{ height: `${heightVh}vh` }}
        className={cn(
          "fixed left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-2xl z-[1100]",
          "transform transition-transform duration-200 ease-out lg:hidden",
          "flex flex-col",
          open ? "translate-y-0" : "translate-y-full",
        )}
      >
        {/* Drag-handle area — tap to close. Visual-only handle bar +
            optional title beside it. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 pt-2 pb-3 px-4 flex flex-col items-center gap-2 border-b border-secondary-200"
        >
          <div className="w-10 h-1.5 bg-secondary-300 rounded-full" />
          {title && (
            <div className="text-sm font-semibold text-secondary-700">
              {title}
            </div>
          )}
        </button>
        {/* Body — scrollable */}
        <div className="flex-1 overflow-auto">{children}</div>
      </div>
    </>
  );
};

export default BottomSheet;
